import {
  CustomReport,
  stafferToNamedDoc,
} from '@principle-theorem/principle-core';
import {
  CustomChartType,
  IBaseChartBuilderConfig,
  IBrand,
  ICustomChartSettings,
  ICustomReport,
  ICustomReportChart,
  IStaffer,
  MeasureReducer,
  QueryScopeConfigId,
  ReportBuilderDataSourceId,
} from '@principle-theorem/principle-core/interfaces';
import {
  ALL_REGIONS,
  asReffable,
  DocumentReference,
  Firestore,
  Transaction,
  uid,
  WithRef,
} from '@principle-theorem/shared';
import { compact, first } from 'lodash';
import { ICustomReportTemplate } from './custom-report-templates';
import { ReportBuilderDataSourceInstance } from './data-sources/report-builder-data-source-instance';
import { CustomReportFilter } from './models/custom-report-filters';

export class CustomReportTemplate {
  static basicDataSourceTemplate(
    dataSource: ReportBuilderDataSourceInstance
  ): ICustomReportTemplate {
    const dateRangeFilter = CustomReportFilter.initDateRange({
      id: dataSource.getDefaultQueryableTimestamp().property.metadata.id,
    });

    const omitDateRange = [ReportBuilderDataSourceId.Patients];
    const staticFilters = omitDateRange.includes(dataSource.id)
      ? dataSource.staticFilters
      : [dateRangeFilter, ...dataSource.staticFilters];

    return {
      name: dataSource.name,
      description: dataSource.description,
      dataSourceId: dataSource.id,
      staticFilters,
      columns: dataSource.getDefaultColumns(),
      chartSections: [
        CustomReport.chartSection(
          'Charts',
          compact([this.getCountChart(dataSource)])
        ),
      ],
      tags: compact([
        dataSource.name,
        'Basic',
        dataSource.isRetired ? 'Retired' : undefined,
      ]),
      regions: ALL_REGIONS,
    };
  }

  static getTemplateName(template: ICustomReportTemplate): string {
    return `My ${template.name} Report`;
  }

  static getCountChart(
    dataSource: ReportBuilderDataSourceInstance
  ):
    | ICustomReportChart<ICustomChartSettings, IBaseChartBuilderConfig>
    | undefined {
    const dataPoint = first(dataSource.getDefaultColumns())?.measure;
    const groupBy = first(
      dataSource.groupByOptions.flatMap((section) => section.options)
    )?.measure;
    if (!dataPoint || !groupBy) {
      return;
    }
    const label = `${dataSource.rowItemName}s`;
    return {
      uid: uid(true),
      type: CustomChartType.NumberSummary,
      label,
      groupBy: groupBy.metadata.id,
      builderData: {
        colourOverride: false,
        plottedOverTime: false,
        accumulateOverTime: false,
      },
      settings: [
        {
          uid: uid(true),
          label,
          dataPoint: dataPoint.metadata.id,
          reducer: MeasureReducer.Count,
        },
      ],
    };
  }

  static async createReportFromTemplate(
    brandRef: DocumentReference<IBrand>,
    template: ICustomReportTemplate,
    creator: WithRef<IStaffer>,
    transaction?: Transaction
  ): Promise<DocumentReference<ICustomReport>> {
    const customReport = CustomReport.init({
      name: CustomReportTemplate.getTemplateName(template),
      description: template.description,
      dataSource: template.dataSourceId,
      queryScopes: {
        [QueryScopeConfigId.Brand]: { brandRef },
        [QueryScopeConfigId.Practice]: { practiceRefs: [] },
      },
      staticFilters: template.staticFilters ?? [],
      chartSections: template.chartSections ?? [],
      columns: template.columns ?? [],
      creator: stafferToNamedDoc(creator),
    });

    return Firestore.addDoc(
      CustomReport.col(asReffable(brandRef)),
      customReport,
      undefined,
      transaction
    );
  }
}
